import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('fencepost_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const res = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(res.data);
        } catch (error) {
          // Only sign the user out on an EXPLICIT auth rejection (401/403).
          // Network hiccups, timeouts, 5xx errors, or offline mode must NOT
          // clear the token — otherwise a page refresh on cellular kicks
          // the user out to the public landing page (real bug, Feb 2026).
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem('fencepost_token');
            setToken(null);
          } else {
            // Transient error — keep the token, optimistically mark the
            // user as authenticated using the token so ProtectedRoute
            // doesn't bounce them to /login. The next successful API call
            // will hydrate the real user record.
            console.warn('Auth init deferred (non-auth error):', status || error?.message);
            setUser({ pending: true });
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    localStorage.setItem('fencepost_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API_URL}/api/auth/register`, data);
    localStorage.setItem('fencepost_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('fencepost_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(res.data);
      } catch (error) {
        console.error('Refresh user error:', error);
      }
    }
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshUser,
      getAuthHeader
    }}>
      {children}
    </AuthContext.Provider>
  );
};
